import { useCallback, useMemo, useState } from 'react'
import { CustomSelectBase, CustomSelectBaseProps } from '..'
import SearchBar from '../../../baseComponents/SearchBar'
import { ChangeValueFunc, Modify } from '../../../utils/globals/types'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { CustomSelectDropDown } from '../DropDown'
import { CustomSelectDropDownBaseProps, CustomSelectDropDownRowProps, CustomSelectDropDownUnsortedProps, DropDownComponentType, DropDownDeciderProps } from '../DropDown/types'
import { CustomSelectSortFunctionTy, DisplayTextFuncTy } from '../types'
import { filterCustomSelectItemsBlocked, sortItemsBlocked } from './function'
import './customselectblocked.sass'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { faLock } from '@fortawesome/pro-light-svg-icons'
import { TwinDictionary } from '../../../utils/globals/dictionary'

export type CustomSelectBlockedItemsProps = Modify<CustomSelectBaseProps, CustomSelectBlockedItemsBaseLogicProps & {
    DropDownComponent?: never
    displayTextFunc?: never
}>

const CustomSelectBlockedItems: React.FC<CustomSelectBlockedItemsProps> = ({ items, blockItemsFnc, sortFunction, sortItems, ...rest}) => {
    const { displayTextFunc, parsedItems } = useCustomSelectBlockedItemsBaseLogic({ items, blockItemsFnc, fieldName: rest.fieldName })
    const Component = DropDownDecider({sortItems, sortFunction})
    return (
        <CustomSelectBase {...rest} items={parsedItems} DropDownComponent={Component} {...{sortItems, sortFunction, displayTextFunc}} />
    )
}

interface CustomSelectBlockedItemsBaseLogicProps {
    fieldName?: string
    blockItemsFnc: (items: TwinDictionary) => TwinDictionary
    items: TwinDictionary
}

const useCustomSelectBlockedItemsBaseLogic = ({ blockItemsFnc, items, fieldName = 'name'}: CustomSelectBlockedItemsBaseLogicProps) => {
    const parsedItems = useMemo(() => blockItemsFnc(items), [items, blockItemsFnc])
    const displayTextFunc: DisplayTextFuncTy = useCallback((items, value) => {
        if (!Array.isArray(items)) {
            return items[value]?.[fieldName]
        }
        return ''
    }, [fieldName])
    return { displayTextFunc, parsedItems }
}

interface DefaultDropDownDeciderProps {
    sortItems?: boolean
    children?: React.ReactNode
    sortFunction?: CustomSelectSortFunctionTy
}
const DropDownDecider = ({ DropDownComponent, sortItems, sortFunction }: DropDownDeciderProps) => {
    if (DropDownComponent) {
        return DropDownComponent
    }
    return DefaultDropDownDecider({ sortItems, sortFunction })
}

const DefaultDropDownDecider = ({ sortItems, sortFunction }: DefaultDropDownDeciderProps) => {
    if (sortFunction || sortItems) {
        return CustomSelectBlockedItemDropDownSorted
    }
    return CustomSelectBlockedItemDropDownUnsorted
}

interface CustomSelectBlockedItemDropDownSortedProps extends CustomSelectDropDownBaseProps { }
const CustomSelectBlockedItemDropDownSorted: React.FC<CustomSelectBlockedItemDropDownSortedProps> = ({ items, children, sortFunction, fieldName='name', ...rest }) => {
    const itemsSorted = useMemo(() => sortFunction ? sortFunction(items) : sortItemsBlocked(items, fieldName), [items, fieldName, sortFunction])
    const { cancelChange } = useCustomSelectBlockedItemsLogic()
    
    return (<CustomSelectDropDown items={itemsSorted} {...rest} rowRenderer={({ ...rowRenderProps }) => {
        const item = itemsSorted[rowRenderProps.index] as TwinDictionary
        const isBlocked = item?.is_blocked
        return (
            <CustomSelectDropDownRowBlockedItems currentvalue={rest.value} onChange={isBlocked ? cancelChange : rest.onChange} item={{ value: item.value, text: item?.[fieldName] }}  {...rowRenderProps} className={isBlocked ? 'is_blocked' : ''} isBlocked={isBlocked ? true : false} />
        )
    }}>{children}</CustomSelectDropDown>)
}

interface CustomSelectBlockedItemDropDownUnsortedProps extends CustomSelectDropDownUnsortedProps {}
const CustomSelectBlockedItemDropDownUnsorted: React.FC<CustomSelectBlockedItemDropDownUnsortedProps> = ({ items, children, fieldName = 'name', ...rest }) => {
    const keys = useMemo(() => Object.keys(items), [items])
    const { cancelChange } = useCustomSelectBlockedItemsLogic()
    return (<CustomSelectDropDown items={keys} {...rest} rowRenderer={({ ...rowRenderProps }) => {
        const item = items[keys[rowRenderProps.index]]
        const isBlocked = item?.is_blocked
        return (
            <CustomSelectDropDownRowBlockedItems currentvalue={rest.value} onChange={isBlocked ? cancelChange : rest.onChange} item={{ value: keys[rowRenderProps.index], text: item?.[fieldName] }}  {...rowRenderProps} className={isBlocked ? 'is_blocked' : ''} isBlocked={isBlocked ? true : false} />
        )
    }}>{children}</CustomSelectDropDown>)
}


export const CustomSelectBlockedItemsSearchbar: React.FC<CustomSelectBlockedItemsProps> = ({ items, blockItemsFnc, sortFunction, sortItems,  ...rest }) => {
    const { displayTextFunc, parsedItems } = useCustomSelectBlockedItemsBaseLogic({ items, blockItemsFnc, fieldName: rest.fieldName })
    return (
        <CustomSelectBase {...rest} items={parsedItems} DropDownComponent={DropDownWithSearchBar} {...{ sortItems, sortFunction, displayTextFunc }} />
    )
}

const DropDownWithSearchBar: DropDownComponentType = ({ ...props }) => {
    const { t } = useTwinTranslation()
    const { searchValue, changeSearchValue, filteredItems } = useDropDownBlockedWithSearchBarLogic({ items: props.items, fieldName: props.fieldName})
    const Component = DefaultDropDownDecider({ sortItems: props.sortItems, sortFunction: props.sortFunction })
    return (
        <Component {...props} items={filteredItems}>
            <SearchBar className='custom_select_search_bar' placeholder={t('search', 'Buscar')} onChange={changeSearchValue} {...{ searchValue }} />
        </Component>
    )
}

interface DropDownBlockedWithSearchBarLogicProps {
    items: TwinDictionary
    fieldName?: string
}
const useDropDownBlockedWithSearchBarLogic = ({ items, fieldName = 'name' }: DropDownBlockedWithSearchBarLogicProps ) => {
    const [searchValue, setSearchValue] = useState<string>('')
    const filteredItems = useMemo(() => filterCustomSelectItemsBlocked(searchValue, items, fieldName), [searchValue, items, fieldName])
    const changeSearchValue: ChangeValueFunc = useCallback((myvalue) => {
        setSearchValue(myvalue)
    }, [setSearchValue])

    return { searchValue, changeSearchValue, filteredItems }
}


const useCustomSelectBlockedItemsLogic = () => {
    const cancelChange: CustomSelectDropDownRowProps['onChange'] = useCallback((_value, e) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])

    return {cancelChange}
}
interface CustomSelectDropDownRowBlockedItemsProps extends CustomSelectDropDownRowProps {
    isBlocked: boolean
}
export const CustomSelectDropDownRowBlockedItems: React.FC<CustomSelectDropDownRowBlockedItemsProps> = ({ className, onChange, isBlocked, ...rest }) => {
    const { changeValue, text, value } = useDropDownRowBlockedLogic({ onChange, ...rest })
    return (
        <div className={'custom_select_value cursor-pointer flex items-center bg-white regular12 text-gray-51 hover:bg-gray-F7 px-15 ' + (className || '')} onClick={changeValue} {...rest} {...value}>
            {isBlocked ?<TwinIcon className='mr-8' icon={faLock}></TwinIcon>: null}
            <span>{text}</span>
        </div>
    )
}

const useDropDownRowBlockedLogic = ({ item, onChange }: CustomSelectDropDownRowProps) => {
    const value = item.value
    const text = item.text

    const changeValue = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        onChange(value, e)
    }, [onChange, value])

    return { changeValue, text, value: {selectValue: value} }
}

export default CustomSelectBlockedItems